/**
 * Watch Index Page - 配信一覧
 * Display list of scheduled and live streams based on event schedules
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import ViewerHeader from '../components/ViewerHeader';
import PointsPurchaseModal from '../components/PointsPurchaseModal';
import MyPageModal from '../components/MyPageModal';
import FavoritesModal from '../components/FavoritesModal';
import NotificationsModal from '../components/NotificationsModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import Banner from '../components/Banner';

import { usePoints } from '../hooks/usePoints';
import { apiClient, type EventSchedule } from '../lib/api-client';
import { formatScheduleTimeRange } from '../lib/date-utils';
import { ROUTES } from '../lib/constants';
import { resolveMediaUrl } from '../lib/media-url';

// Public access: This page is accessible without authentication
// Some features require login (points, favorites, etc.)

export default function WatchIndex() {
    const { t } = useTranslation('watch');
    const navigate = useNavigate();
    const { user, logout, isAdminOrManager } = useAuth();
    const { points, purchasePoints } = usePoints();
    const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
    const [isMyPageModalOpen, setIsMyPageModalOpen] = useState(false);
    const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
    const [schedules, setSchedules] = useState<EventSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [handleName, setHandleName] = useState<string | null>(null);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

    // Check if user is authenticated
    const isAuthenticated = !!user;
    const isViewer = user?.role === 'VIEWER' || user?.role === 'USER';

    // Handler for login button
    const handleLoginClick = useCallback(() => {
        navigate(ROUTES.LOGIN);
    }, [navigate]);

    // Handler for register button
    const handleRegisterClick = useCallback(() => {
        navigate(ROUTES.REGISTER);
    }, [navigate]);

    // Fetch viewer's handle_name
    useEffect(() => {
        if (!isViewer) {
            setHandleName(null);
            return;
        }

        const fetchViewerProfile = async () => {
            try {
                const profile = await apiClient.getMyProfile();
                setHandleName(profile.handle_name);
            } catch (e) {
                console.error('Failed to fetch viewer profile', e);
            }
        };

        fetchViewerProfile();
    }, [user, isViewer]);

    // Fetch unread notification count
    const fetchUnreadNotificationCount = useCallback(async () => {
        if (!isViewer) {
            setUnreadNotificationCount(0);
            return;
        }
        try {
            const response = await apiClient.getUnreadNotificationCount();
            setUnreadNotificationCount(response.unread_count);
        } catch (e) {
            console.error('Failed to fetch unread notification count', e);
        }
    }, [user, isViewer]);

    useEffect(() => {
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    // Handle notifications modal close - refresh unread count since modal marks them as read
    const handleNotificationsModalClose = useCallback(() => {
        setIsNotificationsModalOpen(false);
        // Refresh count after modal closes (notifications are marked as read when opened)
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    // Fetch schedules (status is determined from DB, not in-memory streams)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const schedulesResponse = await apiClient.listEventSchedules({ upcoming_only: false });
                // Filter to show only scheduled and live
                const filteredSchedules = schedulesResponse.schedules.filter(
                    s => s.status === 'scheduled' || s.status === 'live'
                );
                setSchedules(filteredSchedules);
            } catch (err) {
                console.error('Failed to fetch data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Refresh every 10 seconds
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, []);

    const handlePurchasePoints = async (amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000) => {
        await purchasePoints(amount);
    };

    // Format schedule time for display - uses shared date-utils for consistency
    const formatScheduleTime = (startTime: string, endTime: string): string => {
        return formatScheduleTimeRange(startTime, endTime, t('list.today'));
    };

    // Get profile image URL
    const getProfileImageUrl = (schedule: EventSchedule): string | null => {
        if (!schedule.streamer_profile_image_url) return null;
        return resolveMediaUrl(schedule.streamer_profile_image_url);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <ViewerHeader
                points={isAuthenticated ? points : null}
                onPointsClick={() => setIsPointsModalOpen(true)}
                showExitButton={isAdminOrManager()}
                exitButtonLabel={t('navigation.backToConsole', '管理画面に戻る')}
                onExitClick={() => navigate(ROUTES.CONSOLE)}
                title={t('list.headerTitle')}
                username={user?.username}
                onLogout={logout}
                onMyPageClick={() => setIsMyPageModalOpen(true)}
                onFavoritesClick={() => setIsFavoritesModalOpen(true)}
                onNotificationsClick={() => setIsNotificationsModalOpen(true)}
                notificationCount={unreadNotificationCount}
                isAuthenticated={isAuthenticated}
                onLoginClick={handleLoginClick}
                onRegisterClick={handleRegisterClick}
                isAdminOrManager={isAdminOrManager()}
            />

            <div className="container mx-auto px-4 py-6">
                {/* Language Switcher */}
                <div className="flex justify-end mb-4">
                    <LanguageSwitcher variant="buttons" />
                </div>

                <h1 className="text-3xl font-bold text-gray-800 mb-6">{t('list.title')}</h1>

                {/* Banner Area */}
                <Banner
                    location="streaming_list"
                    className="mb-6 overflow-hidden relative w-full pb-[16.67%]"
                    imgClassName="absolute top-0 left-0 w-full h-full object-cover block"
                    showEditButton={isAdminOrManager()}
                />

                <div className="bg-white rounded-none p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-semibold text-gray-800">{t('list.scheduleTitle')}</h2>
                    </div>

                    {loading ? (
                        <div className="text-center py-8 text-gray-500">{t('list.loading')}</div>
                    ) : schedules.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {t('list.noStreams')}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                            {schedules.map((schedule) => {
                                const isLive = schedule.status === 'live';
                                const displayName = schedule.streamer_display_name || schedule.streamer_username || t('list.defaultStreamer');
                                const profileImage = getProfileImageUrl(schedule);
                                const scheduleTime = formatScheduleTime(schedule.scheduled_start, schedule.scheduled_end);
                                const isToday = scheduleTime.startsWith(t('list.today'));

                                return (
                                    <Link
                                        key={schedule.id}
                                        to={`/watch/${schedule.streamer_username}/before-entry?schedule=${schedule.id}`}
                                        className="bg-white rounded-none overflow-hidden shadow-sm border border-gray-200 hover:shadow-md hover:border-pink-300 transition-all cursor-pointer block"
                                    >
                                        <div className={`p-2 text-sm font-bold text-center text-white ${
                                            isLive ? 'bg-pink-500' : isToday ? 'bg-orange-500' : 'bg-gray-700'
                                        }`}>
                                            {isLive ? t('status.live') : scheduleTime}
                                        </div>
                                        <div className="aspect-square bg-gray-200 flex items-center justify-center overflow-hidden">
                                            {profileImage ? (
                                                <img
                                                    src={profileImage}
                                                    alt={displayName}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="text-center">
                                                    <div className="text-4xl mb-2">👩</div>
                                                </div>
                                            )}
                                        </div>
                                        <div className={`p-3 ${
                                            isLive ? 'bg-pink-50' : isToday ? 'bg-orange-100' : 'bg-gray-50'
                                        }`}>
                                            <h3 className="text-base font-bold text-gray-900 text-center truncate">
                                                {displayName}
                                            </h3>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Points Purchase Modal */}
            <PointsPurchaseModal
                isOpen={isPointsModalOpen}
                onClose={() => setIsPointsModalOpen(false)}
                currentPoints={points}
                onPurchase={handlePurchasePoints}
            />

            {/* MyPage Modal */}
            <MyPageModal
                isOpen={isMyPageModalOpen}
                onClose={() => setIsMyPageModalOpen(false)}
                points={points}
                username={user?.username || ''}
                handleName={handleName}
                onPointsClick={() => setIsPointsModalOpen(true)}
                onHandleNameUpdate={(newName) => setHandleName(newName)}
            />

            {/* Favorites Modal */}
            <FavoritesModal
                isOpen={isFavoritesModalOpen}
                onClose={() => setIsFavoritesModalOpen(false)}
                onViewStreamer={(id) => {
                    setIsFavoritesModalOpen(false);
                    navigate(`/watch/${id}`);
                }}
            />

            {/* Notifications Modal */}
            <NotificationsModal
                isOpen={isNotificationsModalOpen}
                onClose={handleNotificationsModalClose}
            />

        </div>
    );
}
